<template>
  <b-modal
    :id="id"
    :visible="isVisible"
    centered
    no-close-on-esc
    hide-header
    hide-footer
    modal-class="login-modal"
    content-class="rounded-3"
    @hidden="onClose"
  >
    <template #default>
      <div class="login-modal-content">
        <div class="text-center mb-3">
          <h3 class="text-white">
            เข้าสู่ระบบ
          </h3>
        </div>

        <validation-observer ref="loginRules" tag="form">
          <b-row>
            <b-col sm="12">
              <input-field
                id="login-phone"
                v-model="form.phone"
                label="เบอร์โทรศัพท์"
                :red="true"
                placeholder="กรุณาระบุเบอร์โทร กรอกเฉพาะตัวเลขเท่านั้น"
                :rules="'required|numeric|digits:10'"
                err-message="กรุณาระบุเบอร์โทรศัพท์ให้ถูกต้อง"
              />
            </b-col>
            <b-col sm="12">
              <validation-provider
                #default="{ errors }"
                name="Password"
                rules="required"
              >
                <b-form-group
                  label="รหัสผ่าน"
                  label-for="login-password"
                  :state="errors.length > 0 ? false : null"
                >
                  <template v-slot:label>
                    รหัสผ่าน <span class="text-danger">*</span>
                  </template>
                  <b-input-group
                    class="input-group-merge"
                    :class="errors.length > 0 ? 'is-invalid' : null"
                  >
                    <b-form-input
                      id="login-password"
                      v-model="form.password"
                      :type="passwordFieldType"
                      class="form-control-merge register-input"
                      :state="errors.length > 0 ? false : null"
                      placeholder="กรุณากรอกรหัสผ่าน"
                    />
                    <b-input-group-append is-text>
                      <feather-icon
                        class="cursor-pointer"
                        :icon="passwordToggleIcon"
                        @click="togglePasswordVisibility"
                      />
                    </b-input-group-append>
                  </b-input-group>
                  <small class="text-danger">{{
                    errors[0] ? 'กรุณากรอกรหัสผ่าน' : ''
                  }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col sm="12" class="mt-1">
              <b-form-checkbox
                v-model="form.remember_me"
                class="remember-me-checkbox"
              >
                จำฉันไว้
              </b-form-checkbox>
            </b-col>
            <b-col sm="12" class="mt-2">
              <b-button
                variant="primary"
                block
                size="lg"
                class="login-btn"
                @click="handleLogin"
              >
                เข้าสู่ระบบ
              </b-button>
            </b-col>
          </b-row>
        </validation-observer>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import {
  BModal,
  BRow,
  BCol,
  BButton,
  BFormGroup,
  BInputGroup,
  BInputGroupAppend,
  BFormInput,
  BFormCheckbox
} from 'bootstrap-vue'
import { required } from '@validations'
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import getCurrentDomain from '@/utils/domain'
import InputField from '../../component/InputField.vue'
import jwt from '../../../auth/jwt/useJwt'
import { setUserData } from '../../../auth/utils'

export default {
  name: 'LoginModal',
  components: {
    ValidationObserver,
    ValidationProvider,
    BModal,
    BRow,
    BCol,
    BButton,
    BFormGroup,
    BInputGroup,
    BInputGroupAppend,
    BFormInput,
    BFormCheckbox,
    InputField
  },
  mixins: [togglePasswordVisibility],
  props: {
    id: {
      type: String,
      default: 'login-modal'
    },
    show: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isVisible: false,
      form: {
        phone: '',
        password: '',
        remember_me: false
      },
      required
    }
  },
  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    }
  },
  watch: {
    show: {
      handler(newVal) {
        this.isVisible = newVal
        if (newVal) {
          this.initializeModal()
        }
      },
      immediate: true
    }
  },
  methods: {
    onClose() {
      this.isVisible = false
      this.$emit('close')
    },
    initializeModal() {
      this.form = {
        phone: '',
        password: '',
        remember_me: false
      }
    },
    async handleLogin() {
      const isValid = await this.$refs.loginRules.validate()
      if (isValid) {
        try {
          const resp = await jwt.login({
            username: this.form.phone,
            password: this.form.password,
            domain: getCurrentDomain(),
            remember_me: this.form.remember_me
          })

          if (resp.data.success === true) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'เข้าสู่ระบบสำเร็จ',
                icon: 'CheckIcon',
                variant: 'success'
              }
            })
            jwt.setToken(resp.data.token)
            setUserData(resp.data.member)
            this.onClose()

            // Reload page to refresh all components with authenticated state
            window.location.reload()
          } else {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง',
                icon: 'XIcon',
                variant: 'danger'
              }
            })
          }
        } catch (error) {
          const errorMessage =
            error.response?.data?.error || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง'
          this.$toast({
            component: ToastificationContent,
            props: {
              title: errorMessage,
              icon: 'XIcon',
              variant: 'danger'
            }
          })
        }
      }
    }
  },
  emits: ['close', 'login']
}
</script>

<style lang="scss">
.login-modal {
  .modal-content {
    background: var(--theme-modal-bg) !important;
    border: none !important;
    border-radius: 15px !important;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
    overflow: hidden;
  }

  .modal-dialog {
    max-width: 400px;
  }
}

:deep(.modal-content) {
  background: var(--theme-modal-bg) !important;
}

.login-modal-content {
  color: white;
  font-weight: 200 !important;
  padding: 20px;

  .login-btn {
    background: var(--theme-gradient-primary);
    border: none;
    border-radius: 8px;
    font-weight: 500;
    padding: 12px;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(var(--theme-primary-rgb), 0.4);
    }
  }

  h3 {
    font-weight: 300;
    margin-bottom: 30px;
  }
}

:deep(.modal-body) {
  background: transparent !important;
  padding: 0 !important;
}

:deep(.modal-backdrop) {
  background-color: rgba(0, 0, 0, 0.8) !important;
}
</style>
